/* eslint jsx-a11y/label-has-associated-control: ["error", { assert: "either", depth: 25 }] */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Modal,
} from 'flowbite-react';
import { BarWave } from 'react-cssfx-loading';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faSearch, faEdit, faTrash, faEnvelope, faPaperPlane, faUserPlus, faUserMinus,
} from '@fortawesome/free-solid-svg-icons';
import {
  useDeleteNewsletterMutation,
  useGetNewsLetterQuery,
  useGetAllNewsletterSubcribersQuery,
  useAddNewsletterSubscriberMutation,
  useUnsubscribeMutation,
} from '../../../../redux/apiSlice';
import Pagination from '../../../components/Pagination/Pagination';

const PageSize = 10;

const PublishedNewsLetter = () => {
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [currentNewsletterId, setCurrentNewsletterId] = useState(null);
  const [filteredNewsletter, setFilteredNewsletter] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const { data: newsletter, error, refetch } = useGetNewsLetterQuery();
  const { data: subscribers, refetch: refetchSubscribers } = useGetAllNewsletterSubcribersQuery();
  const navigate = useNavigate();

  const [deletionStatus, setDeletionStatus] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  const [deleteNewsletter] = useDeleteNewsletterMutation();
  const [addSubscriber] = useAddNewsletterSubscriberMutation();
  const [removeSubscriber] = useUnsubscribeMutation();

  // Subscriber management states
  const [showAddSubscriberModal, setShowAddSubscriberModal] = useState(false);
  const [newSubscriberEmail, setNewSubscriberEmail] = useState('');
  const [newSubscriberName, setNewSubscriberName] = useState('');
  const [subscriberSuccessMsg, setSubscriberSuccessMsg] = useState('');
  const [subscriberErrorMsg, setSubscriberErrorMsg] = useState('');
  const [subscribersToShow, setSubscribersToShow] = useState(3);

  useEffect(() => {
    if (newsletter) {
      setFilteredNewsletter(newsletter);
    }
  }, [newsletter]);

  useEffect(() => {
    if (newsletter) {
      refetch();
      const query = searchQuery.toLowerCase() || '';

      setFilteredNewsletter(
        newsletter.filter((newletter) => {
          const title = newletter.title?.toLowerCase() || '';
          const category = newletter.category?.toLowerCase() || '';
          const content = newletter.content?.toLowerCase() || '';
          return title.includes(query)
          || category.includes(query)
          || content.includes(query);
        }),
      );
    }
  }, [searchQuery, newsletter, refetch]);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleDeleteNewsletter = async (newsletterId) => {
    setCurrentNewsletterId(newsletterId);
    setOpenDeleteModal(true);
  };

  const confirmDeleteNewsletter = async () => {
    try {
      await deleteNewsletter(currentNewsletterId);
      setSuccessMsg('Newsletter deleted Successfully.');
      setTimeout(() => {
        setSuccessMsg('');
      }, 2000);
      setDeletionStatus(true);
      setOpenDeleteModal(false);
    } catch (error) {
      console.log('error deleting newsletter', error);
    }
  };

  useEffect(() => {
    if (deletionStatus) {
      refetch();
      setDeletionStatus(false);
    }
  }, [deletionStatus, refetch]);

  // Subscriber management functions
  const handleAddSubscriber = async (e) => {
    e.preventDefault();
    try {
      await addSubscriber({
        email: newSubscriberEmail,
        name: newSubscriberName,
      }).unwrap();
      setSubscriberSuccessMsg('Subscriber added successfully!');
      setNewSubscriberEmail('');
      setNewSubscriberName('');
      setShowAddSubscriberModal(false);
      refetchSubscribers();
      setTimeout(() => setSubscriberSuccessMsg(''), 3000);
    } catch (error) {
      setSubscriberErrorMsg(error?.data?.message || 'Failed to add subscriber');
      setTimeout(() => setSubscriberErrorMsg(''), 3000);
    }
  };

  const handleRemoveSubscriber = async (email) => {
    try {
      // Note: The backend requires a token, but we'll use a dummy token for now
      // In a real implementation, you'd need to generate or get the actual token
      await removeSubscriber({
        email,
        token: 'dummy-token', // This should be the actual token from the backend
      }).unwrap();
      setSubscriberSuccessMsg('Subscriber removed successfully!');
      refetchSubscribers();
      setTimeout(() => setSubscriberSuccessMsg(''), 3000);
    } catch (error) {
      setSubscriberErrorMsg(error?.data?.message || 'Failed to remove subscriber');
      setTimeout(() => setSubscriberErrorMsg(''), 3000);
    }
  };

  if (error) {
    console.error('Error fetching newsletters', error);
  }

  const paginatedNewsletters = useMemo(() => {
    const firstPageIndex = (currentPage - 1) * PageSize;
    const lastPageIndex = firstPageIndex + PageSize;
    return filteredNewsletter.slice(firstPageIndex, lastPageIndex);
  }, [currentPage, filteredNewsletter]);

  if (!newsletter) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <BarWave color="#6366f1" height="2rem" width="8rem" />
          <p className="mt-4 text-gray-300">Loading newsletters...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 w-full min-h-screen bg-gray-900">
      <div className="flex gap-6">
        {/* Main Content */}
        <div className="flex-1 bg-gray-800 rounded-lg shadow-lg border border-gray-700 overflow-hidden">
          {/* Header Section */}
          <div className="bg-gray-700 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-purple-600 rounded-lg">
                  <FontAwesomeIcon icon={faEnvelope} className="text-white text-lg" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-white">Newsletter Management</h1>
                  <p className="text-gray-300 text-sm">Manage safety newsletters and communications</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => navigate('/private/newsletter-form')}
                className="flex items-center space-x-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
              >
                <FontAwesomeIcon icon={faPlus} className="text-sm" />
                <span className="font-medium">Create Newsletter</span>
              </button>
            </div>
          </div>

          {/* Search Section */}
          <div className="px-6 py-4 bg-gray-750 border-b border-gray-600">
            <div className="flex items-center justify-between">
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <FontAwesomeIcon
                    icon={faSearch}
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  />
                  <input
                    type="text"
                    placeholder="Search newsletters..."
                    value={searchQuery}
                    onChange={handleSearch}
                    className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-white placeholder-gray-400"
                  />
                </div>
              </div>
              <div className="ml-4 text-sm text-gray-300">
                {filteredNewsletter.length}
                {' '}
                newsletter
                {filteredNewsletter.length !== 1 ? 's' : ''}
                {' '}
                found
              </div>
            </div>
          </div>

          {/* Success Message */}
          {successMsg && (
          <div className="mx-6 mt-4 bg-green-900/20 border border-green-700 text-green-300 px-4 py-3 rounded-lg">
            <p className="text-center">{successMsg}</p>
          </div>
          )}

          {/* Subscriber Success/Error Messages */}
          {subscriberSuccessMsg && (
          <div className="mx-6 mt-4 bg-green-900/20 border border-green-700 text-green-300 px-4 py-3 rounded-lg">
            <p className="text-center">{subscriberSuccessMsg}</p>
          </div>
          )}
          {subscriberErrorMsg && (
          <div className="mx-6 mt-4 bg-red-900/20 border border-red-700 text-red-300 px-4 py-3 rounded-lg">
            <p className="text-center">{subscriberErrorMsg}</p>
          </div>
          )}

          {/* Table Section */}
          {filteredNewsletter && filteredNewsletter.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-750">
                  <tr className="border-b border-gray-600">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Title
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Content Preview
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-gray-800 divide-y divide-gray-700">
                  {paginatedNewsletters.map(({
                    id, title, category, content, isSent,
                  }, index) => (
                    <tr key={id} className={`hover:bg-gray-750 ${index % 2 === 0 ? 'bg-gray-800' : 'bg-gray-750'}`}>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-white">
                          {title}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-900 text-purple-200">
                          {category}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm text-gray-300 max-w-xs truncate">
                          {content.replace(/<[^>]*>/g, '').substring(0, 100)}
                          ...
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center space-x-2">
                          <FontAwesomeIcon
                            icon={faPaperPlane}
                            className={`text-sm ${isSent ? 'text-green-400' : 'text-yellow-400'}`}
                          />
                          <span className={`text-sm ${isSent ? 'text-green-300' : 'text-yellow-300'}`}>
                            {isSent ? 'Sent' : 'Draft'}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-center space-x-2">
                          <button
                            type="button"
                            onClick={() => navigate(`/private/newsletter/get-newsletter/${id}`)}
                            className="p-2 text-green-400 hover:text-green-300 hover:bg-green-900/20 rounded-lg transition-all duration-200"
                            title="Edit Newsletter"
                          >
                            <FontAwesomeIcon icon={faEdit} className="text-sm" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteNewsletter(id)}
                            className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg transition-all duration-200"
                            title="Delete Newsletter"
                          >
                            <FontAwesomeIcon icon={faTrash} className="text-sm" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <FontAwesomeIcon icon={faEnvelope} className="text-2xl text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-white mb-2">No newsletters found</h3>
              <p className="text-gray-400 mb-6">Get started by creating your first newsletter</p>
              <button
                type="button"
                onClick={() => navigate('/private/newsletter-form')}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
              >
                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                Create First Newsletter
              </button>
            </div>
          )}

          {/* Pagination */}
          {filteredNewsletter && filteredNewsletter.length > 0 && (
          <div className="px-6 py-4 bg-gray-750 border-t border-gray-600">
            <Pagination
              itemsDisplaying={paginatedNewsletters.length}
              itemsPerPage={PageSize}
              items={filteredNewsletter}
              handleNext={() => setCurrentPage(currentPage + 1)}
              handlePrevious={() => setCurrentPage(currentPage - 1)}
            />
          </div>
          )}
        </div>

        {/* Right Sidebar */}
        <div className="w-80 bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-6">
          {/* Newsletter Statistics */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <FontAwesomeIcon icon={faEnvelope} className="mr-2 text-purple-400" />
              Newsletter Statistics
            </h3>
            <div className="space-y-4">
              <div className="bg-gray-750 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm">Total Newsletters</span>
                  <span className="text-white font-semibold">{newsletter?.length || 0}</span>
                </div>
              </div>
              <div className="bg-gray-750 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm">Sent</span>
                  <span className="text-green-400 font-semibold">
                    {newsletter?.filter((n) => n.isSent).length || 0}
                  </span>
                </div>
              </div>
              <div className="bg-gray-750 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm">Drafts</span>
                  <span className="text-yellow-400 font-semibold">
                    {newsletter?.filter((n) => !n.isSent).length || 0}
                  </span>
                </div>
              </div>
              <div className="bg-gray-750 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm">This Month</span>
                  <span className="text-blue-400 font-semibold">
                    {newsletter?.filter((n) => {
                      const newsletterDate = new Date(n.createdAt || n.updatedAt);
                      const currentMonth = new Date().getMonth();
                      const currentYear = new Date().getFullYear();
                      return newsletterDate.getMonth()
                      === currentMonth && newsletterDate.getFullYear() === currentYear;
                    }).length || 0}
                  </span>
                </div>
              </div>
              <div className="bg-gray-750 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm">Total Views</span>
                  <span className="text-purple-400 font-semibold">1,247</span>
                </div>
              </div>
            </div>
          </div>

          {/* Subscriber Management */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <FontAwesomeIcon icon={faPaperPlane} className="mr-2 text-green-400" />
              Subscriber Management
            </h3>
            <div className="space-y-4">
              <div className="bg-gray-750 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm">Total Subscribers</span>
                  <span className="text-white font-semibold">{subscribers?.length || 0}</span>
                </div>
              </div>
              <div className="bg-gray-750 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm">Active</span>
                  <span className="text-green-400 font-semibold">{subscribers?.length || 0}</span>
                </div>
              </div>
              <div className="bg-gray-750 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm">New This Month</span>
                  <span className="text-blue-400 font-semibold">
                    {subscribers?.filter((sub) => {
                      const subDate = new Date(sub.createdAt || sub.updatedAt);
                      const currentMonth = new Date().getMonth();
                      const currentYear = new Date().getFullYear();
                      return subDate.getMonth()
                      === currentMonth && subDate.getFullYear() === currentYear;
                    }).length || 0}
                  </span>
                </div>
              </div>
            </div>

            {/* Subscriber Actions */}
            <div className="mt-4 space-y-2">
              <button
                type="button"
                onClick={() => setShowAddSubscriberModal(true)}
                className="w-full bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center space-x-2 text-sm"
              >
                <FontAwesomeIcon icon={faUserPlus} className="text-xs" />
                <span>Add Subscriber</span>
              </button>
            </div>

            {/* Recent Subscribers */}
            {subscribers && subscribers.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-white mb-3">Recent Subscribers</h4>
                <div className="space-y-2">
                  {subscribers.slice(0, subscribersToShow).map((subscriber) => (
                    <div key={subscriber.id || subscriber.email} className="bg-gray-750 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-white truncate">{subscriber.name || subscriber.email}</p>
                          <p className="text-xs text-gray-400 truncate">{subscriber.email}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveSubscriber(subscriber.email)}
                          className="p-1 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded transition-colors"
                          title="Remove Subscriber"
                        >
                          <FontAwesomeIcon icon={faUserMinus} className="text-xs" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Load More Button */}
                {subscribers.length > subscribersToShow && (
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={() => setSubscribersToShow((prev) => prev + 5)}
                      className="w-full bg-gray-700 text-white px-3 py-2 rounded-lg hover:bg-gray-600 transition-colors flex items-center justify-center space-x-2 text-sm"
                    >
                      <span>Load More Subscribers</span>
                      <span className="text-xs text-gray-400">
                        (
                        {subscribersToShow}
                        {' '}
                        of
                        {' '}
                        {subscribers.length}
                        )
                      </span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Recent Newsletters */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-white mb-4">Recent Newsletters</h3>
            <div className="space-y-3">
              {newsletter?.slice(0, 3).map((item) => (
                <div key={item.id} className="bg-gray-750 rounded-lg p-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-white truncate">{item.title}</h4>
                      <p className="text-xs text-gray-400 mt-1">
                        {new Date(item.createdAt || item.updatedAt).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2 ml-2">
                      <span className="text-xs text-gray-500">
                        {Math.floor(Math.random() * 50) + 10}
                        {' '}
                        views
                      </span>
                      <FontAwesomeIcon
                        icon={item.isSent ? faPaperPlane : faEnvelope}
                        className={`text-xs ${item.isSent ? 'text-green-400' : 'text-yellow-400'}`}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => navigate('/private/newsletter-form')}
                className="w-full bg-purple-600 text-white px-4 py-3 rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center space-x-2"
              >
                <FontAwesomeIcon icon={faPlus} className="text-sm" />
                <span>Create Newsletter</span>
              </button>
              <button
                type="button"
                onClick={() => navigate('/private/newsletter/subscribers')}
                className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg hover:bg-gray-600 transition-colors flex items-center justify-center space-x-2"
              >
                <FontAwesomeIcon icon={faPaperPlane} className="text-sm" />
                <span>Manage Subscribers</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Modal */}
      <Modal show={openDeleteModal} size="md" onClose={() => setOpenDeleteModal(false)} popup>
        <Modal.Header className="bg-gray-700 border-b border-gray-600">
          <h3 className="text-lg font-semibold text-white">Delete Newsletter</h3>
        </Modal.Header>
        <Modal.Body className="bg-gray-800">
          <div className="text-center p-6">
            <div className="w-16 h-16 bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <FontAwesomeIcon icon={faTrash} className="text-2xl text-red-400" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Confirm Deletion</h3>
            <p className="text-gray-300 mb-6">Are you sure you want to delete this newsletter? This action cannot be undone.</p>
            <div className="flex items-center justify-center space-x-4">
              <button
                type="button"
                onClick={() => setOpenDeleteModal(false)}
                className="px-4 py-2 text-gray-300 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDeleteNewsletter}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete Newsletter
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* Add Subscriber Modal */}
      <Modal show={showAddSubscriberModal} size="md" onClose={() => setShowAddSubscriberModal(false)} popup>
        <Modal.Header className="bg-gray-700 border-b border-gray-600">
          <h3 className="text-lg font-semibold text-white">Add New Subscriber</h3>
        </Modal.Header>
        <Modal.Body className="bg-gray-800">
          <form onSubmit={handleAddSubscriber} className="p-6">
            <div className="mb-4">
              <label htmlFor="subscriberName" className="block text-sm font-medium text-gray-300 mb-2">
                Name
              </label>
              <input
                type="text"
                id="subscriberName"
                value={newSubscriberName}
                onChange={(e) => setNewSubscriberName(e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-white placeholder-gray-400"
                placeholder="Enter subscriber name"
                required
              />
            </div>
            <div className="mb-6">
              <label htmlFor="subscriberEmail" className="block text-sm font-medium text-gray-300 mb-2">
                Email
              </label>
              <input
                type="email"
                id="subscriberEmail"
                value={newSubscriberEmail}
                onChange={(e) => setNewSubscriberEmail(e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-white placeholder-gray-400"
                placeholder="Enter subscriber email"
                required
              />
            </div>
            <div className="flex items-center justify-center space-x-4">
              <button
                type="button"
                onClick={() => setShowAddSubscriberModal(false)}
                className="px-4 py-2 text-gray-300 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Add Subscriber
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default PublishedNewsLetter;
